/**
 * Resolve the URL of a sized image variant.
 *
 * Backend produces 3-4 derivatives per user-uploaded image at upload time:
 *   <uuid>_orig.jpg   archive (lightly compressed original)
 *   <uuid>_lg.jpg     1600px long edge
 *   <uuid>_md.jpg     800px long edge
 *   <uuid>_sm.jpg     256px long edge
 *
 * After Tier 1 (new uploads) and Tier 2 (backfill of legacy assets), every URL
 * stored in the DB ends in `_orig.jpg`. Sized variants are resolved by string
 * replace — no network call, no backend dependency.
 *
 * For an asset that has not yet been backfilled, we fall back to the on-demand
 * variant endpoint `/img/<base_key>/<size>` which 301-redirects to the right
 * S3 URL (generating the derivative on first hit, then caching).
 *
 * Usage:
 *   <img src={imageVariant(worker.main_picture, 'sm')} alt="..." />
 *   <img src={imageVariant(restaurant.pictures[0].url, 'lg')} alt="..." />
 *
 * Pass `null` / `undefined` through unchanged — components that show a
 * placeholder when the URL is missing keep working without extra null checks.
 */
var ORIG_SUFFIX = '_orig.jpg'; // Overloads narrow the return type so call sites that pass a concrete `string`
// never need `?? fallback` defensiveness. `null`/`undefined` inputs pass
// through unchanged for placeholder-rendering components.

export function imageVariant(url) {
  var _process$env$NEXT_PUB;

  var size = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'md';
  if (!url) return url; // Backfilled / Tier 1 path — pure string replace.

  if (url.includes(ORIG_SUFFIX)) {
    return url.replace(ORIG_SUFFIX, "_".concat(size, ".jpg"));
  } // Legacy assets (pre-backfill) still live at `<uuid>.<ext>` on S3. Route
  // through the backend variant endpoint which lazily generates + caches
  // the derivative on S3 and 301-redirects. Strip query string + fragment
  // so pre-signed S3 URLs don't end up as a garbage variant key.


  var last = url.split('/').pop();
  if (!last) return url;
  var baseKey = last.split(/[?#]/)[0];
  if (!baseKey) return url;
  var apiBase = (_process$env$NEXT_PUB = process.env.NEXT_PUBLIC_BASE_API_URL) !== null && _process$env$NEXT_PUB !== void 0 ? _process$env$NEXT_PUB : '';
  return "".concat(apiBase, "/img/").concat(baseKey, "/").concat(size);
}